import type { ReactNode } from 'react';
import { useRef, useEffect } from 'react';
import clsx from 'clsx';
import './EdgeToEdgeTabs.css';

export interface EdgeToEdgeTabItem {
  id: string;
  label: ReactNode;
  /** Доп. содержимое (точка фильтра, счётчик и т.д.) */
  adornment?: ReactNode;
  /** Где показывать adornment относительно подписи (по умолчанию после текста) */
  adornmentPlacement?: 'start' | 'end';
}

export interface EdgeToEdgeTabsProps {
  items: EdgeToEdgeTabItem[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
  'aria-label'?: string;
}

/**
 * Табы на всю ширину: без скруглений, без боковых отступов у списка,
 * вкладки вплотную, активная — синяя полоса сверху.
 */
export function EdgeToEdgeTabs({
  items,
  value,
  onChange,
  className,
  'aria-label': ariaLabel = 'Вкладки',
}: EdgeToEdgeTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;

      let delta: number;
      if (e.shiftKey) {
        delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      } else if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
        delta = e.deltaX;
      } else {
        delta = e.deltaY;
      }

      if (delta === 0) return;

      e.preventDefault();
      el.scrollLeft += delta;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  if (items.length === 0) return null;

  return (
    <div
      ref={scrollRef}
      className={clsx('edge-to-edge-tabs', className)}
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map((item) => {
        const selected = value === item.id;
        const placement = item.adornmentPlacement ?? 'end';
        const adornmentEl = item.adornment ? (
          <span className="edge-to-edge-tabs__tab-adornment">{item.adornment}</span>
        ) : null;
        const labelEl = <span className="edge-to-edge-tabs__tab-label">{item.label}</span>;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            id={`edge-tab-${item.id}`}
            className={clsx('edge-to-edge-tabs__tab', selected && 'edge-to-edge-tabs__tab_selected')}
            onClick={() => onChange(item.id)}
          >
            {placement === 'start' ? (
              <>
                {adornmentEl}
                {labelEl}
              </>
            ) : (
              <>
                {labelEl}
                {adornmentEl}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
